// Per-segment insight card used on the RFM Dashboard grid. Pairs a
// gradient accent with segment narrative + recommended action CTA.

import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { Link } from 'react-router-dom';
import type { RfmAnalysisData, RfmSegmentRow } from './types';
import { segmentMeta } from './segments';

export interface RfmSegmentCardProps {
  row: RfmSegmentRow;
  totalCustomers: number;
  onCreateCampaign?: () => void;
  animationDelay?: number;
  /** The dashboard's window — View Customers lists the segment over the same one. */
  period?: RfmAnalysisData['period'];
}

export function RfmSegmentCard({ row, totalCustomers, onCreateCampaign, animationDelay = 0, period }: RfmSegmentCardProps) {
  const currency = useTenantCurrency();
  const meta = segmentMeta(row.segment);
  if (!meta) return null;

  const pct = totalCustomers > 0 ? (row.count / totalCustomers) * 100 : 0;

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl ${meta.tint} ${meta.darkTint} shadow-md ring-1 ring-neutral-100/70 dark:ring-neutral-700/60 p-5 transition-all duration-200 hover:shadow-xl animate-slide-up`}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      {/* Gradient accent bar */}
      <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${meta.gradient}`} />
      {/* Decorative glow */}
      <div className={`pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${meta.gradient} opacity-20 group-hover:opacity-30 transition-opacity`} />

      <div className="relative flex items-start justify-between gap-3 mb-3">
        <div className={`w-11 h-11 rounded-xl ${meta.iconBg} flex items-center justify-center text-white shadow-lg`}>
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m3-6a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        </div>
        <span className={`text-xs font-bold uppercase tracking-wider ${meta.textAccent}`}>{pct.toFixed(1)}%</span>
      </div>

      <h3 className="relative text-lg font-bold text-neutral-900 dark:text-white">{meta.label}</h3>
      <p className="relative mt-1 text-xs text-neutral-600 dark:text-neutral-400 leading-relaxed">
        {meta.description}
      </p>

      <dl className="relative mt-4 grid grid-cols-3 gap-2 text-xs">
        <div>
          <dt className="text-neutral-500 dark:text-neutral-400">Customers</dt>
          <dd className="mt-0.5 text-lg font-bold text-neutral-900 dark:text-white tabular-nums">{row.count.toLocaleString('en-IN')}</dd>
        </div>
        <div>
          <dt className="text-neutral-500 dark:text-neutral-400">Avg Spend</dt>
          <dd className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-white tabular-nums">{formatPaise(row.avg_spend, currency)}</dd>
        </div>
        <div>
          <dt className="text-neutral-500 dark:text-neutral-400">Avg Visits</dt>
          <dd className="mt-0.5 text-sm font-semibold text-neutral-900 dark:text-white tabular-nums">{row.avg_visits.toFixed(1)}</dd>
        </div>
      </dl>

      <div className="relative mt-4 rounded-lg bg-white/70 dark:bg-neutral-900/40 p-3 ring-1 ring-neutral-100 dark:ring-neutral-800">
        <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Recommended Action</p>
        <p className="mt-1 text-xs text-neutral-700 dark:text-neutral-300">{meta.recommendedAction}</p>
      </div>

      <div className="relative mt-4 flex items-center gap-2">
        <Link
          to={`/customers?segment=${row.segment}${period ? `&period=${period}` : ''}`}
          className={`flex-1 rounded-lg px-3 py-2 text-center text-xs font-semibold text-white bg-gradient-to-r ${meta.gradient} shadow-sm hover:shadow-md transition-shadow`}
        >
          View Customers
        </Link>
        {onCreateCampaign ? (
          <button
            type="button"
            onClick={onCreateCampaign}
            className="rounded-lg bg-white dark:bg-neutral-800 px-3 py-2 text-xs font-semibold text-neutral-700 dark:text-neutral-200 ring-1 ring-neutral-200 dark:ring-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors"
          >
            Campaign
          </button>
        ) : null}
      </div>
    </div>
  );
}
